import {
  DashboardOutlined,
  DatabaseOutlined,
  FieldTimeOutlined,
  FileProtectOutlined,
  IdcardOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
  ScanOutlined,
  TeamOutlined,
  ThunderboltOutlined,
  WarningOutlined,
} from '@ant-design/icons';

import { Bento, StatTile, Tile, TileBody, TileHeader } from '../../../components/shared';
import { Badge, Button, Skeleton } from '../../../components/ui';
import { PageHeader } from '../../../components/shared';
import { useDieuHuong } from '../../../hooks/useDuongDan';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import { KHONG_CO, soVN } from '../../../lib/dinh-dang';
import { SO_NGAY_THONG_KE, TRANG_THAI_PHIEN } from '../constants';
import { useThongKe, useToanVenNhatKy, useTrienKhai } from '../hooks/useEkyc';
import { ruiRoPhanQuyen } from '../nghiep-vu/ekyc';
import type { TrangThaiPhien } from '../types';

/**
 * Tổng quan định danh (`/ekyc`, phân hệ `EKYC_DASHBOARD`).
 *
 * Dựng theo giao diện v3 (ảnh người dùng cung cấp 24/10/2026): bốn ô số liệu, thẻ "Kết nối của
 * service định danh", rồi hai khối "Lý do hay gặp" và "Theo trạng thái" cạnh nhau, với thẻ toàn
 * vẹn nhật ký nằm dưới khối trạng thái.
 *
 * Ba chỗ màn này cố ý **không** làm theo lối thường gặp, mỗi chỗ vì một sự cố có thật:
 *
 * 1. **Tỉ lệ tự động `null` hiện gạch dài, không phải 0%.** Chưa hoàn tất hồ sơ nào mà hiện "0%"
 *    thì đọc thành "chấm rồi và trượt hết" — một con số sai về chính hệ thống của mình.
 * 2. **Thẻ triển khai tô đỏ khi không ai kiểm quyền.** Nó tồn tại để làm hiện ra trên UI việc ai
 *    đang kiểm phân quyền (`02` §7.2) — rủi ro lớn nhất của bản dựng độc lập, và là thứ **không có
 *    lỗi nào, không có gì đỏ** ngoài đúng thẻ này (`00` §9).
 * 3. **Thẻ toàn vẹn nhật ký có nhánh "chưa kiểm được" riêng.** Ở v3 đường `GET /audit/verify` luôn
 *    403 vì gateway thiếu nhánh `audit` (`00` §8). Gộp nhánh ấy vào "chuỗi băm đứt" là biến một lỗi
 *    phân quyền thành một cáo buộc giả mạo dữ liệu.
 */
export function TongQuanEkycView() {
  const { t, locale } = useT();
  const dichLoi = useLoi();
  const dieuHuong = useDieuHuong();

  const thongKe = useThongKe();
  const trienKhai = useTrienKhai();
  const toanVen = useToanVenNhatKy();

  const tk = thongKe.data;
  const tieuDe = t('ekyc.tongQuanTieuDe');

  const lamMoi = () => {
    void thongKe.refetch();
    void trienKhai.refetch();
    void toanVen.refetch();
  };

  /** Số liệu đang tải thì hiện khung xương; chưa có thì gạch dài, không phải số 0. */
  const so = (n: number | undefined | null) =>
    thongKe.isLoading ? <Skeleton className="h-7 w-16" /> : n === null || n === undefined ? KHONG_CO : soVN(n, locale);

  const tiLe = () => {
    if (thongKe.isLoading) return <Skeleton className="h-7 w-16" />;
    // `null` = chưa hoàn tất hồ sơ nào. Hiện 0% ở đây là nói sai về chính mình.
    if (tk?.autoRate === null || tk?.autoRate === undefined) return KHONG_CO;
    return `${soVN(Math.round(tk.autoRate * 1000) / 10, locale)}%`;
  };

  const thoiGian = () => {
    if (thongKe.isLoading) return <Skeleton className="h-7 w-16" />;
    if (tk?.avgMs === null || tk?.avgMs === undefined) return KHONG_CO;
    return `${soVN(Math.round(tk.avgMs / 100) / 10, locale)}s`;
  };

  const tkt = trienKhai.data;
  const canhBaoQuyen = tkt ? ruiRoPhanQuyen(tkt.authorization.enforcedBy, tkt.authorization.selfEnforced) : false;

  const soLonNhat = Math.max(1, ...(tk?.topReasons ?? []).map((r) => r.count));

  return (
    <div className="flex h-full flex-col bg-subtle">
      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        <PageHeader
          breadcrumb={[{ label: t('nav.nhom.dinhDanhEkyc') }, { label: tieuDe }]}
          titleIcon={<DashboardOutlined className="text-primary-600" />}
          title={tieuDe}
          subtitle={t('ekyc.tongQuanMo', { so: SO_NGAY_THONG_KE })}
          actions={
            <Button
              variant="ghost"
              size="md"
              title={t('chung.lamMoi')}
              aria-label={t('chung.lamMoi')}
              onClick={lamMoi}
            >
              <ReloadOutlined className={thongKe.isFetching ? 'animate-spin' : ''} />
            </Button>
          }
        />

        {thongKe.isError && (
          <div role="alert" className="flex flex-wrap items-center gap-3 rounded-lg border border-danger-200 bg-danger-50 p-3">
            <WarningOutlined className="shrink-0 text-danger-600" />
            <span className="min-w-0 text-sm text-danger-700">
              {t('ekyc.loiSoLieu', { loi: dichLoi(thongKe.error) })}
            </span>
            <Button variant="ghost" size="sm" onClick={() => void thongKe.refetch()}>
              {t('chung.thuLai')}
            </Button>
          </div>
        )}

        <Bento>
          <StatTile
            nhan={t('ekyc.tongHoSo')}
            so={so(tk?.total)}
            sub={t('ekyc.tongHoSoSub', { so: SO_NGAY_THONG_KE })}
            icon={<IdcardOutlined />}
            tone="primary"
          />
          <StatTile
            nhan={t('ekyc.dangChoNguoi')}
            so={so(tk?.pending)}
            sub={t('ekyc.dangChoNguoiSub')}
            icon={<TeamOutlined />}
            tone="warning"
          />
          <StatTile
            nhan={t('ekyc.tiLeTuDong')}
            so={tiLe()}
            sub={t('ekyc.tiLeTuDongSub')}
            icon={<ThunderboltOutlined />}
            tone="success"
          />
          <StatTile
            nhan={t('ekyc.thoiGianXuLy')}
            so={thoiGian()}
            sub={t('ekyc.thoiGianXuLySub')}
            icon={<FieldTimeOutlined />}
            tone="violet"
          />
        </Bento>

        {/* Thẻ triển khai — xem chú thích (2) ở đầu tệp. */}
        <Bento>
          <Tile span="col-span-12">
            <TileHeader
              icon={<SafetyCertificateOutlined />}
              tone={canhBaoQuyen ? 'danger' : 'teal'}
              title={t('ekyc.trienKhai')}
              action={
                tkt ? (
                  <Badge variant={canhBaoQuyen ? 'danger' : 'neutral'}>{t(`ekyc.cheDo.${tkt.mode}`)}</Badge>
                ) : undefined
              }
            />
            <TileBody>
              {trienKhai.isLoading ? (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  {[0, 1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              ) : !tkt ? (
                <p className="text-sm text-danger-700">
                  {t('ekyc.loiTrienKhai', { loi: dichLoi(trienKhai.error) })}
                </p>
              ) : (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  <OTrienKhai
                    icon={<TeamOutlined />}
                    nhan={t('ekyc.trienKhaiNguoiDung')}
                    giaTri={tkt.users}
                  />
                  <OTrienKhai
                    icon={<DatabaseOutlined />}
                    nhan={t('ekyc.trienKhaiDuLieu')}
                    giaTri={tkt.database}
                  />
                  <OTrienKhai
                    icon={<SafetyCertificateOutlined />}
                    nhan={t('ekyc.trienKhaiPhanQuyen')}
                    giaTri={
                      tkt.authorization.enforcedBy === 'gateway'
                        ? t('ekyc.trienKhaiGateway')
                        : t('ekyc.trienKhaiTuKiem')
                    }
                    canhBao={canhBaoQuyen ? t('ekyc.trienKhaiCanhBao') : undefined}
                  />
                  <OTrienKhai
                    icon={<ScanOutlined />}
                    nhan={t('ekyc.trienKhaiBocTach')}
                    giaTri={tkt.documentExtraction.label}
                  />
                </div>
              )}
            </TileBody>
          </Tile>
        </Bento>

        <Bento>
          {/* Lý do hay gặp — thanh ngang, đọc từ trên xuống theo số lượt giảm dần. */}
          <Tile span="lg:col-span-7">
            <TileHeader icon={<FileProtectOutlined />} tone="orange" title={t('ekyc.lyDoHayGap')} />
            <TileBody>
              {thongKe.isLoading ? (
                <div className="space-y-2">
                  {[0, 1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-5 w-full" />
                  ))}
                </div>
              ) : !tk || tk.topReasons.length === 0 ? (
                <p className="text-sm text-content-secondary">{t('ekyc.lyDoHayGapRong')}</p>
              ) : (
                <ul className="space-y-2">
                  {tk.topReasons.map((r) => (
                    <li key={r.code} className="flex items-center gap-3">
                      {/*
                       * Mã lý do giữ nguyên, không dịch: `MA_LY_DO` là hợp đồng với đối tác và cơ
                       * quan quản lý (`05` §5) — đổi mã bên phải là đổi hợp đồng.
                       */}
                      <span className="w-56 shrink-0 truncate font-mono text-2xs text-content-secondary" title={r.code}>
                        {r.code}
                      </span>
                      <span className="h-2 min-w-0 flex-1 rounded-full bg-subtle">
                        <span
                          className="block h-2 rounded-full bg-primary-500"
                          style={{ width: `${Math.max(4, (r.count / soLonNhat) * 100)}%` }}
                        />
                      </span>
                      <span className="w-8 shrink-0 text-right text-xs tabular-nums text-content-secondary">
                        {soVN(r.count, locale)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </TileBody>
          </Tile>

          <Tile span="lg:col-span-5">
            <TileHeader icon={<IdcardOutlined />} tone="primary" title={t('ekyc.theoTrangThai')} />
            <TileBody>
              {thongKe.isLoading ? (
                <Skeleton className="h-16 w-full" />
              ) : !tk || Object.keys(tk.byStatus).length === 0 ? (
                <p className="text-sm text-content-secondary">{t('ekyc.theoTrangThaiRong')}</p>
              ) : (
                <ul className="flex flex-wrap gap-2">
                  {Object.entries(tk.byStatus).map(([tt, n]) => {
                    const tong = TRANG_THAI_PHIEN[tt as TrangThaiPhien];
                    return (
                      <li key={tt}>
                        <button
                          type="button"
                          className="rounded-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500"
                          onClick={() => dieuHuong('/ekyc/ho-so')}
                        >
                          <Badge variant={tong.variant}>
                            {t(tong.khoa)} · {soVN(n ?? 0, locale)}
                          </Badge>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </TileBody>
          </Tile>
        </Bento>

        {/* Toàn vẹn nhật ký — xem chú thích (3) ở đầu tệp. */}
        <Bento>
          <Tile span="col-span-12">
            <TileHeader icon={<SafetyCertificateOutlined />} tone="teal" title={t('ekyc.toanVen')} />
            <TileBody>
              <ToanVen
                dangTai={toanVen.isLoading}
                loi={Boolean(toanVen.error)}
                ok={toanVen.data?.ok ?? false}
                checked={toanVen.data?.checked ?? 0}
                brokenAt={toanVen.data?.brokenAt ?? null}
              />
            </TileBody>
          </Tile>
        </Bento>
      </div>
    </div>
  );
}

function OTrienKhai({
  icon,
  nhan,
  giaTri,
  canhBao,
}: {
  icon: React.ReactNode;
  nhan: string;
  giaTri: string;
  canhBao?: string;
}) {
  return (
    <div className="flex gap-2">
      <span className={canhBao ? 'mt-0.5 shrink-0 text-danger-600' : 'mt-0.5 shrink-0 text-content-muted'}>{icon}</span>
      <div className="min-w-0">
        <p className="text-xs text-content-muted">{nhan}</p>
        <p className={canhBao ? 'truncate text-sm font-medium text-danger-700' : 'truncate text-sm font-medium text-content-primary'}>
          {giaTri}
        </p>
        {canhBao && <p className="mt-0.5 text-2xs leading-relaxed text-danger-700">{canhBao}</p>}
      </div>
    </div>
  );
}

function ToanVen({
  dangTai,
  loi,
  ok,
  checked,
  brokenAt,
}: {
  dangTai: boolean;
  loi: boolean;
  ok: boolean;
  checked: number;
  brokenAt: number | null;
}) {
  const { t, locale } = useT();

  if (dangTai) return <Skeleton className="h-10 w-72" />;

  /*
   * Nhánh này KHÔNG được gộp với "chuỗi băm đứt": một lỗi phân quyền (v3: gateway thiếu nhánh
   * `audit` ⇒ 403) đọc thành một cáo buộc giả mạo dữ liệu là sai về bản chất.
   */
  if (loi) {
    return (
      <div className="flex gap-2">
        <WarningOutlined className="mt-0.5 shrink-0 text-content-muted" />
        <div className="min-w-0">
          <Badge variant="neutral">{t('ekyc.toanVenChua')}</Badge>
          <p className="mt-1 text-xs leading-relaxed text-content-secondary">{t('ekyc.toanVenChuaMo')}</p>
        </div>
      </div>
    );
  }

  if (!ok && brokenAt !== null) {
    return (
      <div className="flex gap-2">
        <WarningOutlined className="mt-0.5 shrink-0 text-danger-600" />
        <div className="min-w-0">
          <Badge variant="danger">{t('ekyc.toanVenGay')}</Badge>
          <p className="mt-1 text-xs leading-relaxed text-content-secondary">
            {t('ekyc.toanVenGayMo', { so: soVN(brokenAt, locale) })}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex gap-2">
      <SafetyCertificateOutlined className="mt-0.5 shrink-0 text-success-600" />
      <div className="min-w-0">
        <Badge variant="success">{t('ekyc.toanVenOk')}</Badge>
        <p className="mt-1 text-xs leading-relaxed text-content-secondary">
          {t('ekyc.toanVenOkMo', { so: soVN(checked, locale) })}
        </p>
      </div>
    </div>
  );
}
